/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, Suspense, lazy, startTransition, useState, useEffect, useRef } from 'react';
import type { ShopperProducts } from '@/scapi';
import { Button } from '@/components/ui/button';
import { useProductView } from '@/providers/product-view';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { useCheckAndExecutePendingAction } from '@/hooks/check-and-execute-pending-action';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
// RGBARRY-CUSTOMIZATION: PDP add-to-cart button swaps to an in-cart quantity stepper once the item is in the basket
import { useBasket } from '@/providers/basket';
import PdpCartStepper from '@/components/pdp-cart-stepper';
// END RGBARRY-CUSTOMIZATION

/** @feature-stub Express checkout buttons — remove this import and its JSX below to strip the stub */
const ExpressPayments = lazy(() => import('@/components/checkout/components/express-payments'));

type MorphPhase = 'idle' | 'circle' | 'done';

function triggerHaptic() {
    // iOS Safari: use a silent AudioContext click to engage the taptic engine
    try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, ctx.currentTime);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.01);
        void ctx.close();
    } catch {
        // AudioContext unavailable — no-op
    }
    // Android / non-iOS: vibrate API
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(30);
    }
}

function useMorphAnimation(isAdding: boolean, wasAdding: React.MutableRefObject<boolean>) {
    const [phase, setPhase] = useState<MorphPhase>('idle');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isAdding && !wasAdding.current) {
            wasAdding.current = true;
            setPhase('circle');
        } else if (!isAdding && wasAdding.current) {
            // API returned: move to done pill
            wasAdding.current = false;
            setPhase('done');
            timerRef.current = setTimeout(() => setPhase('idle'), 1800);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isAdding, wasAdding]);

    return phase;
}

interface ProductCartActionsProps {
    product: ShopperProducts.schemas['Product'];
    /** Called immediately before cart action starts (add or update) - useful for optimistic UI like closing modal */
    onBeforeCartAction?: () => void;
    /** Called after successful cart operation completes (add or update) */
    onCartSuccess?: () => void;
    /** Called if cart operation fails (add or update) */
    onCartError?: (error: unknown) => void;
    /** Called immediately before add to wishlist action starts */
    onBeforeAddToWishlist?: () => void;
    /** Called after successful add to wishlist action completes */
    onAddToWishlistSuccess?: () => void;
    /** Called if add to wishlist operation fails */
    onAddToWishlistError?: (error: unknown) => void;
    /**
     * When provided in add mode, renders a compact two-button layout:
     * "Add to Cart" + "Buy It Now" side by side. Express payments, BNPL,
     * wishlist, and share buttons are hidden in this layout.
     * Typically navigates to the PDP for the full purchase flow.
     */
    onBuyNow?: () => void;
}

export default function ProductCartActions({
    product,
    onBeforeCartAction,
    onCartSuccess,
    onCartError,
    onBeforeAddToWishlist,
    onAddToWishlistSuccess,
    onAddToWishlistError,
    onBuyNow,
}: ProductCartActionsProps): ReactElement {
    const { t } = useTranslation('product');
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // Get shared state from context. currentVariant comes from the SAME source
    // that derives canAddToCart (the provider's controlled/URL variant) so the
    // "select all options" message and the Add-to-Cart enabled state can never
    // disagree — e.g. in the Quick Add modal where the variant is passed in as
    // controlled state and a local useCurrentVariant({ product }) would miss it.
    const {
        mode,
        isAddingToOrUpdatingCart,
        canAddToCart,
        currentVariant,
        isMasterOrVariantProduct,
        handleAddToCart,
        handleUpdateCart,
        handleAddToWishlist,
        // RGBARRY-CUSTOMIZATION: stock caps for the PDP in-cart stepper
        stockLevel,
        maxQuantity,
        // END RGBARRY-CUSTOMIZATION
    } = useProductView();

    const isEditMode = mode === 'edit';
    // Compact layout: shown in add mode when a "Buy It Now" handler is provided (e.g. Quick Add modal).
    // Hides express payments, BNPL, wishlist, and share — shopper goes to PDP for those.
    const isCompactAddMode = !isEditMode && !!onBuyNow;

    // Get product ID for pending action matching
    const productToCheck = isMasterOrVariantProduct ? currentVariant : product;
    const currentProductId = productToCheck?.productId || product.id;

    // RGBARRY-CUSTOMIZATION: on the standard PDP layout, once this product/variant is in the
    // basket the Add to Cart button is replaced by an in-cart quantity stepper. The basket is
    // read-only here (no autoLoad) — the line appears after the shopper adds it this session,
    // populated by the add-to-cart action response, so no extra basket GET is issued on PDP load.
    const basket = useBasket();
    const isStandardAddLayout = !isEditMode && !isCompactAddMode && !isProductASet && !isProductABundle;
    const inCartLine = isStandardAddLayout
        ? basket?.productItems?.find((item) => item.productId === currentProductId)
        : undefined;
    // Cap the stepper's increment at the per-product max or the effective stock level.
    const stepperMax = maxQuantity ?? (stockLevel != null && stockLevel > 0 ? stockLevel : undefined);
    // END RGBARRY-CUSTOMIZATION

    // Check for pending actions and execute if they match this product
    // This handles actions that were initiated before authentication (e.g., addToWishlist)
    useCheckAndExecutePendingAction({
        actionName: 'addToWishlist',
        shouldExecute: (params) => params.productId === currentProductId,
        onMatch: async () => {
            const productToAdd = isMasterOrVariantProduct ? currentVariant : product;
            // Call before callback
            onBeforeAddToWishlist?.();
            try {
                await handleAddToWishlist(productToAdd as ShopperProducts.schemas['Variant']);
                // Call success callback after API completes
                onAddToWishlistSuccess?.();
            } catch (error) {
                onAddToWishlistError?.(error);
                throw error;
            }
        },
    });

    const onAddOrUpdateToCart = async () => {
        // Keep edit-mode optimistic close behavior, but for add-mode quick-add we
        // wait for success so the mounted hook can emit toast + open mini-cart.
        if (isEditMode) {
            onBeforeCartAction?.();
        }

        try {
            // Use handleUpdateCart in edit mode, handleAddToCart in add mode
            if (isEditMode) {
                await handleUpdateCart();
            } else {
                await handleAddToCart();
            }
            // Call success callback after API completes
            onCartSuccess?.();
        } catch (error) {
            onCartError?.(error);
        }
    };

    // Pill-morph animation
    const wasAddingRef = useRef(false);
    const morphPhase = useMorphAnimation(isAddingToOrUpdatingCart, wasAddingRef);

    // onAddOrUpdateToCart is recreated every render, so memoizing this wrapper buys nothing
    const handleClickWithHaptic = () => {
        triggerHaptic();
        void onAddOrUpdateToCart();
    };

    // Defer ExpressPayments loading until after initial render to improve Lighthouse performance
    const [shouldLoadExpressPayments, setShouldLoadExpressPayments] = useState(false);

    useEffect(() => {
        // Use startTransition to mark this as non-urgent, allowing initial render to complete first
        startTransition(() => {
            setShouldLoadExpressPayments(true);
        });
    }, []);

    return (
        <div className="mt-6">
            {/* Options Selection Message. role="status" lives on a persistent container so the
                prompt is announced when it appears/clears as the shopper selects variant options. */}
            <div role="status" aria-atomic="true">
                {isMasterOrVariantProduct && !currentVariant && !isProductASet && !isProductABundle && (
                    <span className="text-destructive font-medium">{t('selectAllOptions')}</span>
                )}
            </div>
            <UITarget targetId="sfcc.pdp.tax.productMessage" />

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
                {/* Compact layout (Quick Add modal): Add to Cart + Buy It Now side by side */}
                {isCompactAddMode && !isProductASet && !isProductABundle && (
                    <div className="grid grid-cols-2 gap-3">
                        <Button
                            onClick={() => void onAddOrUpdateToCart()}
                            disabled={!canAddToCart || isAddingToOrUpdatingCart}
                            className="w-full"
                            size="lg">
                            {isAddingToOrUpdatingCart ? t('addingToCart') : t('addToCart')}
                        </Button>
                        <UITarget targetId="sfcc.quickAdd.payments.expressCheckout">
                            <Button
                                onClick={onBuyNow}
                                disabled={!canAddToCart}
                                variant="outline"
                                className="w-full"
                                size="lg">
                                {t('buyItNow')}
                            </Button>
                        </UITarget>
                    </div>
                )}

                {/* RGBARRY-CUSTOMIZATION: once the item is in the basket (add mode only), show the
                    in-cart quantity stepper in place of the Add to Cart button */}
                {!isCompactAddMode && !isEditMode && !isProductASet && !isProductABundle && inCartLine?.itemId && (
                    <PdpCartStepper
                        itemId={inCartLine.itemId}
                        quantity={inCartLine.quantity ?? 1}
                        max={stepperMax}
                        productName={product.name}
                    />
                )}
                {/* END RGBARRY-CUSTOMIZATION */}

                {/* Standard layout: single Add to Cart / Update button with bounce-dots loading animation */}
                {/* RGBARRY-CUSTOMIZATION: hide the button when the in-cart stepper is shown (add mode) */}
                {!isCompactAddMode && !isProductASet && !isProductABundle && !(inCartLine?.itemId && !isEditMode) && (
                    <Button
                        data-testid="add-to-cart"
                        data-slot="add-to-cart-button"
                        size="lg"
                        onClick={isEditMode ? () => void onAddOrUpdateToCart() : handleClickWithHaptic}
                        disabled={!canAddToCart || isAddingToOrUpdatingCart || morphPhase === 'done'}
                        className="w-full relative overflow-hidden text-base font-semibold leading-6">
                        {/* Bouncing dots — shown while API call is in flight */}
                        <span
                            className="absolute inset-0 flex items-center justify-center gap-1.5"
                            style={{ opacity: morphPhase === 'circle' ? 1 : 0, transition: 'opacity 0.15s' }}
                            aria-hidden="true">
                            <span className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:0ms]" />
                            <span className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:150ms]" />
                            <span className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:300ms]" />
                        </span>
                        {/* Button label */}
                        <span style={{ opacity: morphPhase === 'circle' ? 0 : 1, transition: 'opacity 0.15s' }}>
                            {isEditMode ? t('updateCart') : morphPhase === 'done' ? '✓ Added to Cart' : t('addToCart')}
                        </span>
                    </Button>
                )}

                {/* Express Payments — standard layout only, vertical for PDP */}
                {!isCompactAddMode &&
                    !isProductASet &&
                    !isProductABundle &&
                    !isEditMode &&
                    shouldLoadExpressPayments && (
                        <UITarget targetId="sfcc.pdp.payments.expressCheckout">
                            <Suspense fallback={null}>
                                <ExpressPayments
                                    layout="vertical"
                                    separatorPosition="top"
                                    separatorText={t('expressPayments.separatorBuyWith')}
                                    disabled={!canAddToCart}
                                />
                            </Suspense>
                        </UITarget>
                    )}

                <UITarget targetId="sfcc.pdp.after.addToCart" />
                {!isCompactAddMode && !isEditMode && currentProductId && <UITarget targetId="sfcc.pdp.bnpl.message" />}
            </div>
        </div>
    );
}
